<template>
  <component :is="modal ? 'modal' : 'div'" title="Automation Rules" height="400px" ref="modal">
    <template #buttons v-if="doneBtn">
      <b-button variant="link" class="text-white d-none d-sm-inline" size="sm" @click="$refs.modal.close()">DONE
      </b-button>
    </template>
    <b-list-group>
      <b-list-group-item v-for="(e,index) in completed" :key="'old' + index" variant="light">
        <modal-row label="Action">
          {{ actions.find(a => a.value == e.action).text }}
        </modal-row>
        <modal-row label="Days No Sale">
          {{ e.days_no_sale }}
        </modal-row>
        <b-alert v-if="e.executed_date" class="mb-0 mt-1 py-1" show variant="success">Executed on
          {{ e.executed_date | date }}<br><i>{{ e.executed_log }}</i></b-alert>
        <b-alert class="mb-0 mt-1 py-1" show variant="danger" v-if="e.error">
          Error occurred {{ new Date(e.error_date).toLocaleDateString() }}<br><i>{{ e.error }}</i>
        </b-alert>
      </b-list-group-item>

      <draggable v-model="rules" handle=".handle">
        <b-list-group-item v-for="(e,index) in rules" :key="index">
          <b-row no-gutters>
            <b-col cols="1" class="handle my-auto" v-if="!readonly">
              <font-awesome-icon icon="fa-solid fa-align-justify"/>
            </b-col>
            <b-col cols="10" class="pr-3">
              <modal-row label="Action">
                {{ actions.find(f => f.value == e.action).text }}
              </modal-row>
              <modal-row label="Days No Sale">
                <b-form-input :disabled="readonly" v-model="e.days_no_sale" type="number"/>
              </modal-row>

              <template v-if="e.action == 'bestoffer'">
                <modal-row label="Add Best Offer">
                  <b-form-radio-group
                      :disabled="readonly"
                      :options="[{text: 'Yes', value: true}, {text: 'Remove', value: false}]"
                      v-model="e.bestoffer_enable"/>
                </modal-row>
              </template>

              <template v-else-if="e.action == 'adjustment'">
                <modal-row label="Days Since Sale">
                  <b-form-input :disabled="readonly" v-model="e.days_since_sale" type="number"/>
                </modal-row>
                <modal-row label="Adjustment">
                  <b-row no-gutters>
                    <b-col cols="6">
                      <b-form-select class="" size="sm" v-model="e.adjustment"
                                     :disabled="readonly"
                                     :options="adjustments"/>
                    </b-col>
                    <b-col cols="6" class="pl-2" v-if="e.adjustment">
                      <b-input-group size="sm">
                        <b-input-group-prepend is-text
                                               v-if="adjustments.find(v => v.value == e.adjustment).isdollar">
                          $
                        </b-input-group-prepend>
                        <b-form-input :disabled="readonly" v-model="e.amount" type="number"/>
                        <b-input-group-append is-text
                                              v-if="adjustments.find(v => v.value == e.adjustment).ispercent">
                          %
                        </b-input-group-append>
                      </b-input-group>
                    </b-col>
                  </b-row>
                </modal-row>
                <modal-row label="Limit Price"
                           v-if="e.adjustment && !adjustments.find(v => v.value == e.adjustment).nolimit">
                  <b-input-group prepend="$" size="sm" :append="calcTarget(e)">
                    <b-form-input type="number" :disabled="readonly" v-model="e.limit"/>
                  </b-input-group>
                </modal-row>
              </template>

              <template v-else-if="e.action === 'flag'">
                <modal-row label="Tag">
                  <vue-bootstrap-autocomplete class="flex-fill"
                                              input-class="form-control" show-on-focus
                                              size="sm" v-model="e.tag" :data="availableTags"/>
                </modal-row>

                <modal-row label="Add Note">
                  <b-form-input :disabled="readonly" v-model="e.note"/>
                </modal-row>
              </template>
            </b-col>

            <b-col cols="1" class="text-right my-auto" v-if="!readonly">
              <b-button size="sm" @click="del(e)" variant="link">
                <font-awesome-icon icon="fa-solid fa-trash-can"/>
              </b-button>
            </b-col>
          </b-row>

          <b-alert class="mb-0 mt-1 py-1" show variant="success" v-if="e.executed_date">Executed on
            {{ new Date(e.executed_date).toLocaleDateString() }}<br><i>{{ e.executed_log }}</i></b-alert>
          <b-alert class="mb-0 mt-1 py-1" show variant="primary" v-if="index == 0 && e.execute_date">Next to
            execute
            on
            {{ new Date(e.execute_date).toLocaleDateString() }}
          </b-alert>
          <b-alert class="mb-0 mt-1 py-1" show variant="danger" v-if="e.error">
            Error occurred {{ new Date(e.error_date).toLocaleDateString() }}<br><i>{{ e.error }}</i>
          </b-alert>
        </b-list-group-item>
      </draggable>
    </b-list-group>

    <!-- 'modal-<strip>' is the enclosing modal's strip (modal-portal); anything else a plain portal target -->
    <component :is="modalStrip ? 'modal-portal' : 'portal'" :to="modalStrip || portal" v-if="!readonly">
      <div class="w-100">
        <b-input-group class="mt-3 mb-3" prepend="Rule" size="sm">
          <b-form-select v-model="action" :options="actions"/>
          <b-input-group-append size="sm">
            <b-btn variant="primary" size="sm" @click="addRule(action)">Add</b-btn>
          </b-input-group-append>
        </b-input-group>
        <b-input-group class="mb-3" prepend="Template" size="sm" v-if="templates">
          <b-form-select v-model="template" :options="Object.keys(templates)"/>
          <b-input-group-append size="sm">
            <b-btn variant="primary" size="sm" @click="insertTemplate(template)">Insert</b-btn>
          </b-input-group-append>
        </b-input-group>
      </div>
    </component>
  </component>
</template>

<script>
import VueBootstrapAutocomplete from '@vue-bootstrap-components/vue-bootstrap-autocomplete'
import _ from "lodash"

export default {
  name: "auction-automation",

  components: {
    draggable: () => import('vuedraggable'),
    VueBootstrapAutocomplete
  },

  data() {
    return {
      rules: this.value,
      action: 'adjustment',
      template: null,
      availableTags: [],
      add: null,
      actions: [
        {text: 'Adjustment', value: 'adjustment'},
        {text: 'Best Offer', value: 'bestoffer'},
        {text: 'Tag', value: 'flag'}
      ],
      adjustments: [
        {text: '↓ By %', value: 'DP', ispercent: true},
        {text: '↓ By $', value: 'DD', isdollar: true},
        {text: '↑ By %', value: 'IP', ispercent: true},
        {text: '↑ By $', value: 'ID', isdollar: true},
        {text: 'Fixed $', value: 'FP', isdollar: true, nolimit: true}
      ]
    }
  },

  computed: {
    /** 'modal-bottom' -> 'bottom': a strip of the enclosing modal, or null for a plain portal target */
    modalStrip() {
      const m = /^modal-(top|subtop|bottom|footer)$/.exec(this.portal || '')
      return m ? m[1] : null
    },
    templates() {
      if (this.$store.state.settings.defaults.auction_automation_templates && Object.keys(this.$store.state.settings.defaults.auction_automation_templates).length)
        return this.$store.state.settings.defaults.auction_automation_templates
      else
        return null
    }
  },

  props: {
    portal: {
      type: String,
      default: 'modal-bottom'
    },
    modal: Boolean,
    value: Array,
    completed: Array,
    doneBtn: Boolean,
    readonly: Boolean,
    price: null,
    scrollId: {
      type: String,
      default: 'vue-modal-body'
    }
  },

  watch: {
    rules(v) {
      this.$emit('input', v)
    }
  },

  created() {
    this.template = this.templates ? Object.keys(this.templates)[0] : null
    this.asyncAction('/api/auction/tags', 'GET').then(v => this.availableTags = v)
  },

  methods: {
    calcTarget(e) {
      if (e.action != 'adjustment')
        return null

      if (!e.limit || !this.price || !e.amount || !e.days_no_sale)
        return null

      if ((e.adjustment == 'DP' || e.adjustment == 'DD') && e.limit > this.price)
        return null
      else if ((e.adjustment == 'IP' || e.adjustment == 'ID') && e.limit < this.price)
        return null

      if (e.adjustment == 'DP' || e.adjustment == 'IP') {
        const adj = e.adjustment == 'DP' ? -1 : 1;

        const result = Math.ceil(Math.log(e.limit / this.price) / Math.log(1 + (adj * (e.amount / 100))))
        return (result * e.days_no_sale) + " Days"
      } else if (e.adjustment == 'DD')
        return Math.ceil((this.price - e.limit) / e.amount) + ' Days'
      else if (e.adjustment == 'ID')
        return Math.ceil((this.limit - this.price) / e.amount) + ' Days'

      return null
    },

    addRule(action) {
      const rule = {action}
      if (action === 'bestoffer')
        rule.bestoffer_enable = true
      else if (action === 'adjustment')
        rule.adjustment = 'DP'
      else if (action === 'flag')
        rule.tag = ''

      this.rules.push(rule)

      this.$nextTick(() => {
        const objDiv = document.getElementById(this.scrollId)
        objDiv.scrollTop = objDiv.scrollHeight;
      })
    },
    del(element) {
      this.rules.splice(this.rules.indexOf(element), 1)
    },
    insertTemplate(template) {
      this.rules = this.rules.concat(_.cloneDeep(this.templates[template]))
    }
  }
}
</script>

<style scoped>

</style>